import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import SiteFooter from '@/components/SiteFooter';
import { Reveal, SectionLabel, useSiteContent } from '@/components/Editorial';
import {
  ArrowRight, ArrowUpRight, Check, Info,
  Eye, MessageSquare, BadgeCheck,
  Upload, Users, TrendingUp,
} from 'lucide-react';

const HERO_BG = 'https://www.thelistingroom.com/assets/images/Group-1.jpg';

const BUYING_ICONS = [Eye, MessageSquare, BadgeCheck];
const SELLING_ICONS = [Upload, Users, TrendingUp];
const BUYING_TAGS = ['Browse', 'Negotiate', 'Complete'];
const SELLING_TAGS = ['Submit', 'Exchange', 'Reward'];

export default function About() {
  const navigate = useNavigate();
  const c = useSiteContent('about');
  const svc = useSiteContent('services');
  if (!c) return <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}><Navbar /></div>;

  return (
    <div style={{ backgroundColor: '#fff', color: '#000', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${HERO_BG}')` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(10,10,10,0.78) 0%, rgba(10,10,10,0.35) 55%, rgba(10,10,10,0.15) 100%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.6) 0%, rgba(10,10,10,0) 40%)' }} />
        <div className="relative container-app py-32 md:py-40" style={{ color: '#fff' }}>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] mb-6 text-white/60">{c.hero.label}</p>
            <h1 className="font-bold mb-7" style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', lineHeight: 1.02, letterSpacing: '-0.03em', maxWidth: '960px' }}>
              {c.hero.heading}
            </h1>
            <p className="text-base text-white/70 mb-9" style={{ maxWidth: '620px' }}>{c.hero.p}</p>
            <button onClick={() => navigate('/register')}
              className="inline-flex items-center gap-3 px-9 py-4 text-sm font-semibold uppercase tracking-wider"
              style={{ backgroundColor: '#fff', color: '#000' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'none'; }}>
              {c.hero.cta} <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b" style={{ borderColor: '#E5E5E5' }}>
        <div className="container-app py-24 md:py-32">
          <Reveal><SectionLabel index="01">Why Us</SectionLabel></Reveal>
          <Reveal delay={80}>
            <h2 className="font-bold mb-14" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 3rem)', lineHeight: 1.05, letterSpacing: '-0.03em', maxWidth: '760px' }}>
              {c.pillars.heading}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3" style={{ borderTop: '1px solid #000', borderLeft: '1px solid #E5E5E5' }}>
            {c.pillars.items.map((p, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="bento-cell p-8 h-full" style={{ borderRight: '1px solid #E5E5E5', borderBottom: '1px solid #E5E5E5', minHeight: '300px' }}>
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-2xl font-bold">{p.title}</h3>
                    <span className="text-xs font-semibold tabular-nums bento-muted" style={{ color: '#999' }}>0{i + 1}</span>
                  </div>
                  <ul className="space-y-3">
                    {p.points.map(pt => (
                      <li key={pt} className="flex items-start gap-2.5 text-sm bento-muted" style={{ color: '#444' }}>
                        <Check size={15} className="flex-shrink-0 mt-0.5" /> {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Buying — dark process flow */}
      <section style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
        <div className="container-app py-24 md:py-32">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-semibold tabular-nums text-white/50">(02)</span>
              <span className="w-8 h-px bg-white/25" />
              <span className="text-xs uppercase tracking-[0.25em] text-white/50">{c.buying.heading}</span>
            </div>
            <h2 className="font-bold mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
              How to buy
            </h2>
            <p className="text-sm text-white/50 mb-16" style={{ maxWidth: '440px' }}>
              Three simple steps from discovery to ownership — with no intermediary in between.
            </p>
          </Reveal>

          <div className="hidden md:flex items-center mb-10" style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '2rem' }}>
            {c.buying.steps.map((_, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center text-xs font-bold" style={{ border: '1px solid rgba(255,255,255,0.4)', color: '#fff' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{BUYING_TAGS[i]}</span>
                </div>
                {i < c.buying.steps.length - 1 && (
                  <div className="flex-1 flex items-center px-6">
                    <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} />
                    <ArrowRight size={12} className="text-white/25 mx-1 flex-shrink-0" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {c.buying.steps.map((s, i) => {
              const Icon = BUYING_ICONS[i] || Eye;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="h-full p-8 transition-all duration-300 cursor-default"
                    style={{ border: '1px solid rgba(255,255,255,0.1)', minHeight: '280px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.backgroundColor = 'transparent'; }}>
                    <div className="flex items-start justify-between mb-8">
                      <div className="w-14 h-14 flex items-center justify-center flex-shrink-0" style={{ border: '1px solid rgba(255,255,255,0.25)' }}>
                        <Icon size={22} strokeWidth={1.5} />
                      </div>
                      <span className="text-5xl font-bold tabular-nums" style={{ color: 'rgba(255,255,255,0.08)', lineHeight: 1 }}>0{i + 1}</span>
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.25em] mb-3 text-white/40">{BUYING_TAGS[i]}</p>
                    <h3 className="text-xl font-bold mb-3 text-white">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-white/55">{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Selling — white process flow */}
      <section className="border-b" style={{ borderColor: '#E5E5E5', backgroundColor: '#fff' }}>
        <div className="container-app py-24 md:py-32">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-semibold tabular-nums" style={{ color: '#000' }}>(03)</span>
              <span className="w-8 h-px" style={{ backgroundColor: '#000' }} />
              <span className="text-xs uppercase tracking-[0.25em]" style={{ color: '#666' }}>{c.selling.heading}</span>
            </div>
            <h2 className="font-bold mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', lineHeight: 1, letterSpacing: '-0.03em' }}>
              How to sell
            </h2>
            <p className="text-sm mb-16" style={{ color: '#666', maxWidth: '440px' }}>
              List your work, connect with buyers, and keep every penny — without the auction house middleman.
            </p>
          </Reveal>

          <div className="hidden md:flex items-center mb-10" style={{ borderTop: '1px solid #000', paddingTop: '2rem' }}>
            {c.selling.steps.map((_, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center text-xs font-bold bg-black text-white">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.2em]" style={{ color: '#999' }}>{SELLING_TAGS[i]}</span>
                </div>
                {i < c.selling.steps.length - 1 && (
                  <div className="flex-1 flex items-center px-6">
                    <div className="flex-1 h-px" style={{ backgroundColor: '#E5E5E5' }} />
                    <ArrowRight size={12} className="mx-1 flex-shrink-0" style={{ color: '#CCC' }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="grid md:grid-cols-3" style={{ borderLeft: '1px solid #E5E5E5', borderTop: '1px solid #E5E5E5' }}>
            {c.selling.steps.map((s, i) => {
              const Icon = SELLING_ICONS[i] || Upload;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="bento-cell h-full p-8" style={{ borderRight: '1px solid #E5E5E5', borderBottom: '1px solid #E5E5E5', minHeight: '280px' }}>
                    <div className="flex items-start justify-between mb-8">
                      <div className="w-14 h-14 flex items-center justify-center flex-shrink-0 bento-icon" style={{ border: '1px solid #000' }}>
                        <Icon size={22} strokeWidth={1.5} />
                      </div>
                      <span className="text-5xl font-bold tabular-nums" style={{ color: '#F0F0F0', lineHeight: 1 }}>0{i + 1}</span>
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: '#999' }}>{SELLING_TAGS[i]}</p>
                    <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                    <p className="text-sm leading-relaxed bento-muted" style={{ color: '#666' }}>{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={200}>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-6" style={{ border: '1px solid #E5E5E5', backgroundColor: '#FAFAFA' }}>
              <div className="flex-1">
                <p className="text-sm font-semibold mb-1">0% commission on every sale</p>
                <p className="text-xs" style={{ color: '#888' }}>From listing to completion, you keep the full agreed price. Optional add-on services are the only fees.</p>
              </div>
              <button onClick={() => navigate('/register')}
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 text-xs font-semibold uppercase tracking-wider flex-shrink-0 transition-colors duration-200"
                style={{ backgroundColor: '#000', color: '#fff' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
                Start Selling <ArrowRight size={14} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Optional Services Comparison Table */}
      <section className="border-b" style={{ borderColor: '#E5E5E5', backgroundColor: '#fff' }}>
        <div className="container-app py-24 md:py-32">
          <Reveal>
            <SectionLabel index="04">Add-On Services</SectionLabel>
            <h2 className="font-bold mb-4" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 3rem)', lineHeight: 1.05, letterSpacing: '-0.03em', maxWidth: '760px' }}>
              Optional Listing Services
            </h2>
            <p className="text-sm mb-14" style={{ color: '#666', maxWidth: '560px' }}>
              Available from Messums Listings — select the level of support you need. Each package is a one-time fee per listing.
            </p>
          </Reveal>

          <Reveal delay={80}>
            <div className="overflow-x-auto" data-testid="services-comparison-table">
              <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '560px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
                    <th className="py-4 px-6 text-left text-xs font-semibold uppercase tracking-[0.2em] w-1/2" style={{ borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                      Package
                    </th>
                    {(svc?.packages || []).map((pkg, i) => (
                      <th key={i} className="py-4 px-6 text-center text-xs font-semibold uppercase tracking-[0.2em]" style={{ borderRight: i < (svc.packages.length - 1) ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                        {pkg.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(svc?.features || []).map((feat, idx) => {
                    const checks = feat.included || [];
                    const pkgCount = (svc?.packages || []).length;
                    return (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#FAFAFA' : '#fff', borderBottom: '1px solid #E5E5E5' }}>
                        <td className="py-4 px-6 text-sm" style={{ borderRight: '1px solid #E5E5E5' }}>
                          <span className="flex items-center gap-2">
                            <span>{feat.name}</span>
                            {feat.tooltip && (
                              <span title={feat.tooltip} className="cursor-help flex-shrink-0">
                                <Info size={13} style={{ color: '#C0C0C0' }} />
                              </span>
                            )}
                          </span>
                        </td>
                        {checks.map((has, ci) => (
                          <td key={ci} className="py-4 px-6 text-center" style={{ borderRight: ci < pkgCount - 1 ? '1px solid #E5E5E5' : 'none' }}>
                            {has && (
                              <span className="inline-flex items-center justify-center w-6 h-6 mx-auto" style={{ backgroundColor: '#000', borderRadius: '50%' }}>
                                <Check size={12} color="#fff" strokeWidth={2.5} />
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {/* Price row */}
                  <tr style={{ borderTop: '2px solid #000' }}>
                    <td className="py-5 px-6 text-sm font-semibold" style={{ borderRight: '1px solid #E5E5E5' }}>Price</td>
                    {(svc?.packages || []).map((pkg, i) => (
                      <td key={i} className="py-5 px-6 text-center" style={{ borderRight: i < (svc.packages.length - 1) ? '1px solid #E5E5E5' : 'none' }}>
                        <span className="text-xl font-bold tabular-nums">{pkg.price}</span>
                        <span className="block text-[10px] uppercase tracking-wider mt-0.5" style={{ color: '#999' }}>per listing</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={160}>
            <div className="mt-10 text-center">
              <button onClick={() => navigate('/register')} data-testid="services-signup-cta"
                className="inline-flex items-center gap-3 px-10 py-4 text-sm font-semibold uppercase tracking-wider transition-colors duration-300"
                style={{ backgroundColor: '#000', color: '#fff' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.color = '#000'; e.currentTarget.style.boxShadow = 'inset 0 0 0 1px #000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#000'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.boxShadow = 'none'; }}>
                Sign Up Today <ArrowRight size={16} />
              </button>
              <p className="text-xs mt-4" style={{ color: '#999' }}>Membership is free — fees only apply when you list or choose a service.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Messums */}
      <section style={{ backgroundColor: '#0A0A0A', color: '#fff' }}>
        <div className="container-app py-24 md:py-32 max-w-4xl">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-6 text-white/50">Our Heritage</p>
            <h2 className="font-bold mb-6" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1.05, letterSpacing: '-0.03em' }}>
              {c.messums.heading}
            </h2>
            <p className="text-base text-white/70 mb-8" style={{ maxWidth: '640px' }}>{c.messums.p}</p>
            <a href={c.messums.cta_link} target="_blank" rel="noreferrer" className="editorial-link inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider">
              {c.messums.cta} <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
